import {
  ContactFields,
  ContactInput,
  fetchContacts,
  fetchContactsVariables,
  PaginationFields,
  updateContact
} from '@/types/graphql/schemaTypes';
import { contactService } from '@/services/ContactService';
import { ContactState, RootState } from '@/types/store';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import { ApolloQueryResult } from 'apollo-client';
import { FetchResult } from 'apollo-link';
import get from 'lodash/get';

// initial state
const state: ContactState = {
  contacts: [],
  upsertContact: null,
  deleteContact: '',
  totalCount: 0,
  refresh: false
};

// getters
const getters: GetterTree<ContactState, RootState> = {
  contacts(contactState): ContactFields[] {
    return [...contactState.contacts];
  },
  totalCount(contactState): number {
    return contactState.totalCount;
  },
  refresh(contactState): boolean {
    return contactState.refresh;
  }
};

// actions
const actions: ActionTree<ContactState, RootState> = {
  async fetchContacts({ commit }, args: fetchContactsVariables): Promise<void> {
    contactService.fetchContacts(args.contactArgs, args.pagination)
      .then((response: ApolloQueryResult<fetchContacts>): void => {
        commit('setContacts', response.data.contacts);
        commit('setPagination', response.data.contactsPagination);
        commit('setRefresh', false);
      });
  },
  async createContact({ commit }, contactArgs: ContactInput): Promise<void> {
    contactService.createContact(contactArgs).then((): void => {
      commit('clearUpsert');
      commit('setRefresh', true);
      commit('clearUpsert');
    });
  },
  async updateContact({ commit }, contactArgs: ContactInput): Promise<void> {
    contactService.updateContact(contactArgs)
      .then((response: FetchResult<updateContact, Record<string, any>>): void => {
        commit('updateContact', get(response, 'data.updateContact'));
        commit('clearUpsert');
      });
  },
  async deleteContact({ commit, state }, contactArgs: ContactInput): Promise<void> {
    contactService.deleteContact(contactArgs).then((): void => {
      commit('setContacts', [...state.contacts.filter(c => c.id !== contactArgs.id)]);
    });
  },
  async setUpsertContact({ commit }, contact: ContactFields): Promise<void> {
    commit('setUpsertContact', contact);
  },
  async clearUpsert({ commit }): Promise<void> {
    commit('clearUpsert');
  },
  async setRefresh({ commit }): Promise<void> {
    commit('setRefresh');
  }
};

// mutations
const mutations: MutationTree<ContactState> = {
  setContacts(contactState, contacts: ContactFields[]): void {
    contactState.contacts = contacts;
  },
  setPagination(contactState, pagination: PaginationFields): void {
    contactState.totalCount = pagination.totalCount;
  },
  setUpsertContact(contactState, upsertContact: ContactInput): void {
    contactState.upsertContact = { ...upsertContact };
  },
  setDeleteContact(contactState, id: string): void {
    contactState.deleteContact = id;
  },
  clearUpsert(contactState): void {
    contactState.upsertContact = null;
  },
  updateContact(contactState, contactArgs: ContactFields): void {
    contactState.contacts.splice(contactState.contacts.findIndex((obj: any) => {
      return obj.id === contactArgs.id;
    }), 1, contactArgs);
  },
  setRefresh(contactState, refresh: boolean): void {
    contactState.refresh = refresh;
  }
};

export const contact: Module<ContactState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
